import type { WhiteLabelConfig } from '@v2/types';

export type PublicPartnerConfig = Pick<
  WhiteLabelConfig,
  | 'citiesSection'
  | 'darkLogo'
  | 'lightLogo'
  | 'primaryColor'
  | 'slug'
  | 'sportsDestination'
  | 'sublogoUrl'
>;

const PARTNER_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MAX_PARTNER_SLUG_LENGTH = 100;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const normalizePartnerSlug = (value: string) =>
  value.trim().toLowerCase();

export const parsePartnerSlug = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const slug = normalizePartnerSlug(value);

  if (
    !slug ||
    slug.length > MAX_PARTNER_SLUG_LENGTH ||
    !PARTNER_SLUG_PATTERN.test(slug)
  ) {
    return null;
  }

  return slug;
};

export const isPublicPartnerConfig = (
  value: unknown
): value is PublicPartnerConfig => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.slug === 'string' &&
    typeof value.primaryColor === 'string' &&
    typeof value.lightLogo === 'string' &&
    typeof value.darkLogo === 'string' &&
    typeof value.sublogoUrl === 'string' &&
    Array.isArray(value.citiesSection) &&
    Array.isArray(value.sportsDestination)
  );
};

export const toPublicPartnerConfig = (
  config: WhiteLabelConfig
): PublicPartnerConfig => ({
  citiesSection: config.citiesSection,
  darkLogo: config.darkLogo,
  lightLogo: config.lightLogo,
  primaryColor: config.primaryColor,
  slug: config.slug,
  sportsDestination: config.sportsDestination,
  sublogoUrl: config.sublogoUrl,
});
